/* The public pages' layout. Brand values come from theme/tokens.css; nothing here defines a color of its own. */

.page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--mist);
  overflow-x: clip;
  /* Brand sheet v0.2 lede color; promote to tokens.css. */
  --text-lede: #3b4b66;
  font-size: 17px;
  line-height: 1.55;
  color: var(--text-body);
  /* Marketing CTAs are orange; the portal's own primary stays pear. */
  --brand-fill: var(--orange-600);
  --brand-hover: var(--orange-700);
}

/* Running-text links: pear-700, navy on hover (sheet). Classed links (buttons, nav, footer) keep their own colors. */
.main a:not([class]) {
  color: var(--pear-700);
}

.main a:not([class]):hover {
  color: var(--navy);
}

.main {
  flex: 1;
}

.container {
  width: 100%;
  max-width: var(--content-max);
  margin: 0 auto;
  padding: 0 var(--space-5);
}

@media (max-width: 639px) {
  .container {
    padding: 0 var(--space-4);
  }
}

/* Header */

.header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid var(--border);
}

.headerInner {
  max-width: var(--content-max);
  margin: 0 auto;
  padding: var(--space-3) var(--space-5);
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.homeLink {
  display: inline-flex;
  text-decoration: none;
  border-radius: 6px;
}

.menuButton {
  display: none;
  margin-left: auto;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--control-height);
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--white);
  font-weight: 700;
  font-size: var(--text-ui);
  cursor: pointer;
}

.menuIcon,
.menuIcon::before,
.menuIcon::after {
  display: block;
  width: 16px;
  height: 2px;
  background: var(--navy);
  border-radius: 1px;
}

.menuIcon {
  position: relative;
}

.menuIcon::before,
.menuIcon::after {
  content: '';
  position: absolute;
  left: 0;
}

.menuIcon::before {
  top: -5px;
}

.menuIcon::after {
  top: 5px;
}

.nav {
  margin-left: auto;
}

.navList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.navLink {
  display: inline-flex;
  align-items: center;
  min-height: var(--control-height);
  padding: 0 12px;
  border-radius: var(--radius-control);
  color: var(--text);
  font-weight: 700;
  font-size: var(--text-ui);
  text-decoration: none;
}

.navLink:hover {
  color: var(--pear-700);
  background: var(--pear-50);
}

.navCta {
  margin-left: var(--space-2);
}

/* Phone width: the links fold into a disclosure menu. Without JavaScript the menu stays open below the header. */
@media (max-width: 859px) {
  .headerInner {
    flex-wrap: wrap;
    padding: var(--space-2) var(--space-4);
  }
  /* entry-client marks <html class="js"> before hydrating; until then the links show as a plain list. */
  :global(html.js) .menuButton {
    display: inline-flex;
  }
  .nav {
    flex-basis: 100%;
    margin-left: 0;
  }
  :global(html.js) .nav {
    display: none;
  }
  :global(html.js) .navOpen {
    display: block;
  }
  .navList {
    flex-direction: column;
    align-items: stretch;
    padding: var(--space-2) 0 var(--space-3);
  }
  .navCta {
    margin: var(--space-2) 0 0;
  }
}

/* Sections */

.section {
  padding: 72px 0;
  scroll-margin-top: 72px;
}

.sectionWhite {
  background: var(--white);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.sectionSky {
  background: var(--sky-50);
  border-top: 1px solid var(--info-border);
  border-bottom: 1px solid var(--info-border);
}

.sectionNavy {
  background: var(--navy);
  color: var(--white);
}

/* Dark surfaces: plain navy, or the network image under a navy overlay (hero and closing sections). A photo can
   replace public/marketing/backdrop.svg; the overlay keeps text at contrast either way. */
.sectionImage,
.hero {
  background:
    linear-gradient(100deg, rgba(18, 39, 73, 0.88) 0%, rgba(18, 39, 73, 0.6) 50%, rgba(18, 39, 73, 0.2) 100%),
    url('/marketing/backdrop.svg') center / cover no-repeat,
    var(--navy);
  color: var(--white);
}

.sectionNavy,
.sectionImage,
.hero {
  --text-lede: var(--on-navy-soft);
}

.sectionNavy h1,
.sectionNavy h2,
.sectionNavy h3,
.sectionImage h1,
.sectionImage h2,
.sectionImage h3,
.hero h1,
.hero h2 {
  color: var(--white);
}

.sectionNavy .body,
.sectionImage .body {
  color: var(--on-navy-soft);
}

.sectionNavy .small,
.sectionImage .small {
  color: var(--on-navy-muted);
}

.main .sectionNavy a:not([class]),
.main .sectionImage a:not([class]),
.main .hero a:not([class]) {
  color: var(--sky);
}

.main .sectionNavy a:not([class]):hover,
.main .sectionImage a:not([class]):hover,
.main .hero a:not([class]):hover {
  color: var(--white);
}

.sectionNavy :focus-visible,
.sectionImage :focus-visible,
.hero :focus-visible {
  outline-color: var(--sky);
}

/* An orange rule above every section heading: the pop that ties the page together. */
.section h2::before {
  content: '';
  display: block;
  width: 44px;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--orange) 0 60%, var(--sky) 60% 100%);
  margin-bottom: var(--space-4);
}

@media (max-width: 639px) {
  .section {
    padding: 48px 0;
  }
}

.eyebrow {
  font-size: var(--text-ui);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--orange-600);
}

.sectionNavy .eyebrow,
.sectionImage .eyebrow,
.hero .eyebrow {
  color: var(--orange);
}

.sectionHead {
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.sectionHead h2 {
  font-size: 28px;
}

.lede {
  font-size: 18px;
  line-height: 1.5;
  color: var(--text-lede);
}


.body {
  color: var(--text-body);
  font-size: 17px;
  line-height: 1.6;
}

/* Hero */

.hero {
  padding: 88px 0 96px;
}

/* On dark grounds the secondary call to action is sky. */
.ctaOnDark {
  background: var(--sky);
  border-color: var(--sky);
  color: var(--navy);
}

.ctaOnDark:hover {
  background: var(--white);
  border-color: var(--white);
  color: var(--navy);
}

.heroGrid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: var(--space-7);
  align-items: center;
}

.heroCopy {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* The smaller line under the hero subhead. */
.heroSupporting {
  font-size: var(--text-md);
  line-height: 1.5;
  color: var(--text-lede);
}

.heroCopy h1 {
  font-size: clamp(34px, 4.6vw, 52px);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

@media (max-width: 959px) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-6);
  }
  .hero {
    padding: 48px 0 56px;
  }
}

.ctaRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

/* Two columns: copy beside a visual */

.split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-7);
  align-items: start;
}

.splitWide {
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
}

.splitVideo {
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  align-items: center;
}

@media (max-width: 959px) {
  .split,
  .splitWide,
  .splitVideo {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-6);
  }
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* Steps and cards */

.steps {
  list-style: none;
  counter-reset: step;
  margin: 0 0 var(--space-6);
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 859px) {
  .steps {
    grid-template-columns: minmax(0, 1fr);
  }
}

.stepCard,
.card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.stepCard {
  position: relative;
  counter-increment: step;
}

.stepCard::before {
  content: counter(step);
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--orange-600);
  color: var(--white);
  font-family: var(--font-heading);
  font-weight: 600;
  margin-bottom: var(--space-2);
}

.stepCard:nth-child(3n + 2)::before {
  background: var(--sky);
  color: var(--navy);
}

.stepCard:nth-child(3n)::before {
  background: var(--navy);
}

/* Lucide icons: a tinted badge on cards, an inline mark before a line. Orange-600 on orange-50 and navy on sky-50
   both clear 3:1 for graphics. */
.iconBadge {
  display: inline-grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--orange-50);
  color: var(--orange-600);
  margin-bottom: var(--space-2);
}

.card:nth-child(even) .iconBadge,
.stepCard:nth-child(3n + 2) .iconBadge {
  background: var(--sky-50);
  color: var(--synced-fg);
}

.stepIcon {
  position: absolute;
  top: var(--space-5);
  right: var(--space-5);
}

.stepIcon .iconBadge {
  margin: 0;
}

.inlineIcon {
  flex: none;
  color: var(--orange-600);
}

.iconHeading {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.iconHeading .inlineIcon {
  color: var(--synced-fg);
}

.card {
  border-top: 4px solid var(--orange);
  transition:
    transform 150ms ease,
    box-shadow 150ms ease;
}

.card:nth-child(even) {
  border-top-color: var(--sky);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px -24px rgba(18, 39, 73, 0.45);
}

@media (prefers-reduced-motion: reduce) {
  .card {
    transition: none;
  }
  .card:hover {
    transform: none;
  }
}

.stepCard h3,
.card h3 {
  font-size: var(--text-md);
  font-family: var(--font-body);
  font-weight: 700;
  letter-spacing: 0;
}

.stepCard p,
.card p {
  color: var(--text-body);
  font-size: var(--text-ui);
  line-height: 1.6;
}

.cards {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 959px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 639px) {
  .cards {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Two columns: four cards sit 2x2 on desktop and tablet, stacked on phones (the base .cards rule above). */
.cards.cardsTwo {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 639px) {
  .cards.cardsTwo {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* A card's closing text link sits at the bottom, whatever the body length. */
.cardLink {
  margin-top: auto;
  padding-top: var(--space-2);
  font-weight: 700;
}

/* Question and answer rows: question left, answer right; stacked on phones. Always open, not an accordion. */
.qaRows {
  margin: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border);
}

.qaRow {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: var(--space-5);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--border);
}

.qaRow dt {
  font-weight: 700;
  color: var(--text);
}

.qaRow dd {
  margin: 0;
  color: var(--text-body);
  line-height: 1.6;
}

@media (max-width: 767px) {
  .qaRow {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-2);
  }
}

/* Lists */

.checkList,
.plainList {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.checkList li,
.plainList li {
  position: relative;
  padding-left: 28px;
  color: var(--text-body);
  line-height: 1.6;
}

.checkList li::before {
  content: '';
  position: absolute;
  left: 2px;
  top: 6px;
  width: 12px;
  height: 7px;
  border-left: 2px solid var(--orange-600);
  border-bottom: 2px solid var(--orange-600);
  transform: rotate(-45deg);
}

.plainList li::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 10px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--border-strong);
}

.sectionNavy .plainList li,
.sectionImage .plainList li {
  color: var(--on-navy-soft);
}

.sectionNavy .plainList li::before,
.sectionImage .plainList li::before {
  background: var(--orange);
}

.sectionNavy .checkList li,
.sectionImage .checkList li {
  color: var(--on-navy-soft);
}

.sectionNavy .checkList li::before,
.sectionImage .checkList li::before {
  border-color: var(--orange);
}

/* Short numbered steps beside the demo video. */
.miniSteps {
  list-style: none;
  counter-reset: m;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.miniSteps li {
  counter-increment: m;
  display: flex;
  gap: var(--space-3);
  align-items: baseline;
  color: var(--text-body);
}

.miniSteps li::before {
  content: counter(m);
  flex: none;
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--orange-600);
}

.promptLine {
  font-weight: 700;
  color: var(--text);
}

.videoFrame {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-panel);
  overflow: hidden;
  background: var(--navy);
  box-shadow:
    0 0 0 6px var(--white),
    0 24px 48px -24px rgba(18, 39, 73, 0.55);
}

.videoFrame iframe,
.videoFrame video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.numbered {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: n;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.numbered li {
  counter-increment: n;
  position: relative;
  padding: var(--space-4) var(--space-4) var(--space-4) 60px;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
  color: var(--text-body);
  line-height: 1.55;
}

.numbered li::before {
  content: counter(n);
  position: absolute;
  left: var(--space-4);
  top: var(--space-4);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--orange-600);
  color: var(--white);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-ui);
}

.numbered li:nth-child(even)::before {
  background: var(--sky);
  color: var(--navy);
}

.leadItem strong {
  color: var(--text);
}

/* Proof strip and data handling */

.proof {
  list-style: none;
  margin: var(--space-6) 0;
  padding: var(--space-4) 0;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3) var(--space-5);
  font-weight: 700;
  font-size: var(--text-ui);
  color: var(--text);
}

.proof li:not(:last-child) {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}


.proof li:last-child {
  font-weight: 400;
  color: var(--text-body);
  flex-basis: 100%;
}

.dataBlock {
  background: var(--white);
  border: 1px solid var(--border);
  border-left: 4px solid var(--sky);
  border-radius: var(--radius-row);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.dataBlock h3 {
  font-size: var(--text-lg);
}

.sectionImage .form {
  box-shadow: 0 24px 48px -24px rgba(0, 0, 0, 0.5);
}

.closing {
  margin-top: var(--space-5);
  font-weight: 700;
  color: var(--text);
}

.onNavy {
  color: var(--white);
}

/* FAQ: native details/summary, so it opens with the keyboard and works without JavaScript */

.faq {
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.faqItem {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
}

.faqItem[open] {
  border-color: var(--orange);
  box-shadow: inset 4px 0 0 var(--orange);
}

.faqItem summary {
  list-style: none;
  cursor: pointer;
  padding: var(--space-4) var(--space-5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  border-radius: var(--radius-row);
}

.faqItem summary::-webkit-details-marker {
  display: none;
}

.faqItem summary::after {
  content: '';
  flex: none;
  width: 9px;
  height: 9px;
  border-right: 2px solid var(--orange-600);
  border-bottom: 2px solid var(--orange-600);
  transform: rotate(45deg);
  transition: transform 150ms ease;
}

.faqItem[open] summary::after {
  transform: rotate(-135deg);
}

.faqQuestion {
  font-family: var(--font-body);
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: 0;
}

.faqAnswer {
  padding: 0 var(--space-5) var(--space-5);
  color: var(--text-body);
  line-height: 1.6;
}

/* Contact form */

.contactGrid {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: var(--space-7);
  align-items: start;
}

@media (max-width: 959px) {
  .contactGrid {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-5);
  }
}

/* HubSpot's meetings scheduler: it posts its own height once loaded (islands/BookMeeting.tsx). */
.meetings {
  display: block;
  width: 100%;
  min-width: 0;
  height: 680px;
  border: 0;
}

.small {
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.errorSummary {
  border: 1px solid var(--held-fg);
  background: var(--held-bg);
  color: var(--held-fg);
  border-radius: var(--radius-row);
  padding: var(--space-3) var(--space-4);
  font-weight: 700;
}

.thanks {
  background: var(--synced-bg);
  color: var(--synced-fg);
  border-radius: var(--radius-panel);
  padding: var(--space-5);
  font-weight: 700;
}

/* Fit check */

/* One column: the answer needs the width. */
.fitHero {
  max-width: 920px;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.fitBox {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  box-shadow: 0 12px 32px -24px rgba(18, 39, 73, 0.35);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.chip {
  min-height: 36px;
  padding: 0 14px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border);
  background: var(--mist);
  color: var(--text);
  font-size: var(--text-ui);
  font-weight: 700;
  cursor: pointer;
}

.chip:hover {
  border-color: var(--pear-500);
  background: var(--pear-50);
}

.result {
  margin-top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.verdict {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
}

.verdictPill {
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-pill);
  padding: 8px 20px;
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-xl);
}

.verdictPill[data-tone='fit'] {
  background: var(--synced-bg);
  color: var(--synced-fg);
}

.verdictPill[data-tone='review'] {
  background: var(--info-bg);
  color: var(--info-fg);
  box-shadow: inset 0 0 0 2px var(--sky);
}

.verdictPill[data-tone='notFit'] {
  background: var(--neutral-bg);
  color: var(--navy);
}

.result:focus {
  /* Focus moves here so screen readers start at the answer; it is not a control, so no ring. */
  outline: none;
}

.resultGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 767px) {
  .resultGrid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.resultBlock {
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
  padding: var(--space-4);
  background: var(--white);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.resultBlock h3 {
  font-size: var(--text-md);
}

.resultBlock ul,
.resultBlock ol {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: var(--text-ui);
  color: var(--text-body);
}

.matched {
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

.verdictTable {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  overflow: hidden;
}

.verdictTable caption {
  text-align: left;
}

.verdictTable th,
.verdictTable td {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.verdictTable thead th {
  background: var(--mist);
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.verdictTable tbody tr:last-child th,
.verdictTable tbody tr:last-child td {
  border-bottom: 0;
}

.verdictTable tbody th {
  white-space: nowrap;
  width: 1%;
}

@media (max-width: 479px) {
  .verdictTable tbody th {
    white-space: normal;
  }
}

/* Footer */

.footer {
  background: var(--navy);
  color: var(--on-navy-soft);
  padding: var(--space-6) 0;
  border-top: 4px solid transparent;
  border-image: linear-gradient(90deg, var(--orange), var(--sky)) 1;
}

.footerInner {
  max-width: var(--content-max);
  margin: 0 auto;
  padding: 0 var(--space-5);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4) var(--space-6);
}

.footerLinks {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
  font-size: var(--text-ui);
}

.footerLinks a {
  color: var(--white);
  text-decoration: none;
}

.footerLinks a:hover {
  color: var(--sky);
  text-decoration: underline;
}

.footer :focus-visible {
  outline-color: var(--sky);
}

.copyright {
  font-size: var(--text-sm);
}

/* Development-only markers (never in a production build) */

.pending {
  position: relative;
  outline: 2px dashed var(--dev-marker);
  outline-offset: 4px;
}

.pendingTag,
.todo {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--dev-marker-bg);
  color: var(--dev-marker-fg);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  vertical-align: middle;
}

.todo {
  border: 1px dashed var(--dev-marker);
}

/* Reference pages (/hubspot-app, /support, /security) */

.docBody {
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.docBody h2 {
  margin: 0;
}

.docText {
  margin: 0;
  color: var(--text-body);
  line-height: 1.6;
}

.docH3 {
  margin: var(--space-3) 0 0;
  font-size: var(--text-lg);
}

.docSteps {
  margin: 0;
  padding-left: 22px;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  color: var(--text-body);
  line-height: 1.6;
}

.docLinks {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.docLinks a {
  font-weight: 700;
}

/* A wide table scrolls inside its own box on a phone instead of the page. */
.docTableWrap {
  overflow-x: auto;
}

.docTable {
  min-width: 560px;
  width: 100%;
  border-collapse: collapse;
}

.docTable tbody th {
  white-space: normal;
  width: auto;
  font-weight: 700;
  overflow-wrap: anywhere;
}
